import React, {
  useState,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import UserContext from '../../../../../context/UserContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import AnnouncementContext from '../../../../context/AnnouncementContext';
import useQuickSearch from '../../../../../hooks/useQuickSearch';
import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import {
  HeaderRow,
  TitleContainer,
  Title,
  SubTitle,
} from '../../../../../components/v2/Header';
import { InfoTag } from '../../../../../components/Tags';
import GenericDataGrid from '../../../../components/GenericDataGrid';
import QuickSearchToolbar from '../../../../components/QuickSearchToolbar';
import LabelCheckbox from '../../../../components/LabelCheckbox';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import useToast from '../../../../hooks/useToast';
import { CommunityType, GENERIC_COMMUNITY } from '../../../../Model/Community';
import CommunityFormModal from '../CommunityFormModal';

import { TabContainer } from '../../styles';
import {
  TagChips,
  MutedCell,
  Actions,
  StyledEditIcon,
  StyledArchiveIcon,
} from './styles';
import texts from './texts.json';

const GENERIC_TYPE = 'GENERIC';

const TYPE_LABELS = {
  [GENERIC_TYPE]: texts.typeGeneric,
  [CommunityType.POD]: texts.typePod,
  [CommunityType.CUSTOM]: texts.typeCustom,
  [CommunityType.TAG]: texts.typeTag,
};

// Type filter checkboxes, in display order. All checked by default (show everything).
const TYPE_KEYS = [GENERIC_TYPE, CommunityType.POD, CommunityType.CUSTOM, CommunityType.TAG];

const CommunitiesManager = () => {
  const { userId: coachId } = useContext(UserContext);
  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  const { communities, isReady } = useContext(AnnouncementContext);
  const { showToast } = useToast();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [communityToArchive, setCommunityToArchive] = useState(null);
  const [visibleTypes, setVisibleTypes] = useState(TYPE_KEYS);

  const openCreateModal = useCallback(() => {
    setEditingCommunity(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback(async (community) => {
    try {
      setIsProcessing(true);
      const hasPosts = await community.hasPosts();
      if (hasPosts) {
        showToast(texts.hasPostsToast, { warning: true });
        return;
      }
      setEditingCommunity(community);
      setIsModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  }, [showToast]);

  const requestArchive = useCallback((community) => {
    setCommunityToArchive(community);
  }, []);

  const confirmArchive = useCallback(async () => {
    if (!communityToArchive) {
      return;
    }
    try {
      setIsProcessing(true);
      await communityToArchive.archive(loggedInUserId);
      showToast(texts.archivedToast, { success: true });
      setCommunityToArchive(null);
    } finally {
      setIsProcessing(false);
    }
  }, [communityToArchive, loggedInUserId, showToast]);

  const toggleType = useCallback((type, isChecked) => {
    setVisibleTypes((prev) => (isChecked ? [...prev, type] : prev.filter((t) => t !== type)));
  }, []);

  // One flat row list, ordered Generic → Pod → Custom → Tag (DataGrid renders in array order).
  const rows = useMemo(() => {
    const toRow = (community) => ({
      id: community.id,
      name: community.name,
      type: community.type,
      typeLabel: TYPE_LABELS[community.type] || community.type,
      tags: community.type === CommunityType.TAG ? community.customTags : [],
      community,
      isManageable: community.type === CommunityType.CUSTOM || community.type === CommunityType.TAG,
    });
    const genericRows = GENERIC_COMMUNITY.map((generic) => ({
      id: generic.id,
      name: generic.name,
      type: GENERIC_TYPE,
      typeLabel: TYPE_LABELS[GENERIC_TYPE],
      tags: [],
      community: null,
      isManageable: false,
    }));
    const orderedCommunities = [CommunityType.POD, CommunityType.CUSTOM, CommunityType.TAG]
      .flatMap((type) => communities.filter((community) => community.type === type).map(toRow));
    return [...genericRows, ...orderedCommunities];
  }, [communities]);

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: texts.colName,
      flex: 3,
      quickSearch: true,
    },
    {
      field: 'typeLabel',
      headerName: texts.colType,
      flex: 2,
    },
    {
      field: 'tags',
      headerName: texts.colTags,
      flex: 3,
      sortable: false,
      renderCell: ({ row }) => (row.tags.length ? (
        <TagChips>
          {row.tags.map((tag) => (
            <InfoTag key={tag}>{tag}</InfoTag>
          ))}
        </TagChips>
      ) : (
        <MutedCell>—</MutedCell>
      )),
    },
    {
      field: 'actions',
      headerName: texts.colActions,
      flex: 3,
      sortable: false,
      renderCell: ({ row }) => (row.isManageable ? (
        <Actions>
          <PrimaryButton
            variant="light"
            size="small"
            icon={<StyledEditIcon />}
            disabled={isProcessing}
            onClick={() => handleEdit(row.community)}
          >
            {texts.edit}
          </PrimaryButton>
          <PrimaryButton
            variant="danger"
            size="small"
            icon={<StyledArchiveIcon />}
            disabled={isProcessing}
            onClick={() => requestArchive(row.community)}
          >
            {texts.archive}
          </PrimaryButton>
        </Actions>
      ) : null),
    },
  ], [isProcessing, handleEdit, requestArchive]);

  const { filteredRows: quickSearchRows, toolbarProps } = useQuickSearch(rows, columns);
  const visibleRows = useMemo(
    () => quickSearchRows.filter((row) => visibleTypes.includes(row.type)),
    [quickSearchRows, visibleTypes],
  );

  const filterTools = useMemo(
    () => TYPE_KEYS.map((type) => ({
      id: `community-type-${type}`,
      Component: LabelCheckbox,
      props: {
        isChecked: visibleTypes.includes(type),
        description: TYPE_LABELS[type],
        onChange: (isChecked) => toggleType(type, isChecked),
      },
    })),
    [visibleTypes, toggleType],
  );

  return (
    <TabContainer>
      <HeaderRow>
        <TitleContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.subTitle}</SubTitle>
        </TitleContainer>
        <PrimaryButton onClick={openCreateModal}>{texts.newCommunity}</PrimaryButton>
      </HeaderRow>
      <GenericDataGrid
        rows={visibleRows}
        columns={columns}
        components={{ Toolbar: QuickSearchToolbar }}
        componentsProps={{
          toolbar: {
            ...toolbarProps,
            placeholder: texts.searchPlaceholder,
            filterTools,
          },
        }}
      />
      {isModalOpen && (
        <CommunityFormModal
          isOpen={isModalOpen}
          onDidDismiss={() => setIsModalOpen(false)}
          coachId={coachId}
          community={editingCommunity}
        />
      )}
      <ConfirmDialog
        isOpen={!!communityToArchive}
        onConfirm={confirmArchive}
        onCancel={() => setCommunityToArchive(null)}
        confirmDisabled={isProcessing}
        dialogTexts={{
          title: texts.archiveConfirmTitle,
          content: texts.archiveConfirmContent,
          confirm: texts.archiveConfirmButton,
          cancel: texts.cancelButton,
        }}
      />
      <LoadingOverlay isLoading={!isReady || isProcessing} />
    </TabContainer>
  );
};

export default CommunitiesManager;
